import React, { useEffect, useRef } from 'react';
import LogoSvg from '../../Images/Logo.svg';

const LOGO_URL = LogoSvg;

const LogoLoader = ({ size = 200, message = '' }) => {
    const canvasRef = useRef(null);
    const animRef = useRef(null);
    const stateRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const W = canvas.width, H = canvas.height;
        const cx = W / 2, cy = H / 2;

        const SAMPLE_STEP = 3;
        const ALPHA_MIN = 40;
        const MAX_PUSH = 95;
        const JITTER = 26;
        const LOGO_SCALE = 0.52;
        const DOT_SIZE = 1.4;
        const DETOUR_PROBABILITY = 0.35;

        const T_EXPLODE = 1800;
        const T_ROTATE_EXP_1 = 2400;
        const T_TO_POINT = 1400;
        const T_FROM_POINT = 1400;
        const T_ROTATE_EXP_2 = 2000;
        const T_RETURN = 1800;
        const T_ROTATE_INT = 2400;

        const easeOut = x => 1 - (1 - x) * (1 - x);
        const easeInOutCubic = x => x < 0.5 ? 4*x*x*x : 1 - Math.pow(-2*x + 2, 3) / 2;
        const lerp = (a, b, t) => a + (b - a) * t;
        const clamp01 = x => Math.max(0, Math.min(1, x));

        let particles = [];
        let cycleStart = performance.now();
        let cycleDetour = false;
        let cycleDir = 1;
        let cycleTotal = 0;

        const calcTotal = (detour) =>
            T_EXPLODE + T_ROTATE_EXP_1 +
            (detour ? (T_TO_POINT + T_FROM_POINT + T_ROTATE_EXP_2) : 0) +
            T_RETURN + T_ROTATE_INT;

        const startNewCycle = (now) => {
            cycleStart = now;
            cycleDetour = Math.random() < DETOUR_PROBABILITY;
            cycleDir = Math.random() < 0.5 ? -1 : 1;
            cycleTotal = calcTotal(cycleDetour);
        };

        const ensureCycle = (now) => {
            while (now - cycleStart >= cycleTotal) {
                cycleStart += cycleTotal;
                cycleDetour = Math.random() < DETOUR_PROBABILITY;
                cycleDir = Math.random() < 0.5 ? -1 : 1;
                cycleTotal = calcTotal(cycleDetour);
            }
        };

        const rotateCanvas = (angle) => {
            ctx.translate(cx, cy);
            ctx.rotate(angle);
            ctx.translate(-cx, -cy);
        };

        const buildParticles = (img) => {
            const off = document.createElement('canvas');
            off.width = W; off.height = H;
            const ox = off.getContext('2d', { willReadFrequently: true });
            ox.clearRect(0, 0, W, H);

            const scale = Math.min(W / img.width, H / img.height) * LOGO_SCALE;
            const dw = img.width * scale;
            const dh = img.height * scale;
            const dx = (W - dw) / 2;
            const dy = (H - dh) / 2;

            ox.drawImage(img, dx, dy, dw, dh);
            const data = ox.getImageData(0, 0, W, H).data;

            particles = [];
            for (let y = 0; y < H; y += SAMPLE_STEP) {
                for (let x = 0; x < W; x += SAMPLE_STEP) {
                    const i = (y * W + x) * 4;
                    const a = data[i + 3];
                    if (a > ALPHA_MIN) {
                        const vx = x - cx, vy = y - cy;
                        const len = Math.hypot(vx, vy) || 1;
                        const nx = vx / len, ny = vy / len;
                        const push = MAX_PUSH * (0.5 + Math.random() * 0.6);

                        particles.push({
                            x0: x, y0: y,
                            x1: x + nx * push + (Math.random() - 0.5) * JITTER,
                            y1: y + ny * push + (Math.random() - 0.5) * JITTER,
                            r: data[i], g: data[i + 1], b: data[i + 2], a
                        });
                    }
                }
            }
        };

        const render = (now) => {
            ensureCycle(now);
            const t = now - cycleStart;
            ctx.clearRect(0, 0, W, H);

            let cursor = 0;
            const a0 = cursor, a1 = cursor + T_EXPLODE; cursor = a1;
            const b0 = cursor, b1 = cursor + T_ROTATE_EXP_1; cursor = b1;

            let c0=0, c1=0, d0=0, d1=0, e0=0, e1=0;
            if (cycleDetour) {
                c0 = cursor; c1 = cursor + T_TO_POINT; cursor = c1;
                d0 = cursor; d1 = cursor + T_FROM_POINT; cursor = d1;
                e0 = cursor; e1 = cursor + T_ROTATE_EXP_2; cursor = e1;
            }

            const f0 = cursor, f1 = cursor + T_RETURN; cursor = f1;
            const g0 = cursor, g1 = cursor + T_ROTATE_INT;

            let mode = 'normal';
            let kExplode = 0, kToPoint = 0, kFromPoint = 0, angle = 0;

            if (t < a1) {
                kExplode = easeOut(clamp01((t - a0) / (a1 - a0)));
                mode = 'explode';
            } else if (t < b1) {
                kExplode = 1;
                angle = cycleDir * 2 * Math.PI * clamp01((t - b0) / (b1 - b0));
                mode = 'explode';
            } else if (cycleDetour && t < c1) {
                kToPoint = easeInOutCubic(clamp01((t - c0) / (c1 - c0)));
                mode = 'toPoint';
            } else if (cycleDetour && t < d1) {
                kFromPoint = easeOut(clamp01((t - d0) / (d1 - d0)));
                mode = 'fromPoint';
            } else if (cycleDetour && t < e1) {
                angle = cycleDir * 2 * Math.PI * clamp01((t - e0) / (e1 - e0));
                mode = 'explode';
                kExplode = 1;
            } else if (t < f1) {
                kExplode = 1 - easeInOutCubic(clamp01((t - f0) / (f1 - f0)));
                mode = 'explode';
            } else {
                angle = cycleDir * 2 * Math.PI * clamp01((t - g0) / (g1 - g0));
                mode = 'normal';
            }

            ctx.save();
            if (angle !== 0) rotateCanvas(angle);

            for (const p of particles) {
                let x, y;
                if (mode === 'normal') { x = p.x0; y = p.y0; }
                else if (mode === 'explode') { x = lerp(p.x0, p.x1, kExplode); y = lerp(p.y0, p.y1, kExplode); }
                else if (mode === 'toPoint') { x = lerp(p.x1, cx, kToPoint); y = lerp(p.y1, cy, kToPoint); }
                else { x = lerp(cx, p.x1, kFromPoint); y = lerp(cy, p.y1, kFromPoint); }

                let alpha = p.a / 255;
                if (mode !== 'normal') alpha *= 0.88;

                ctx.fillStyle = `rgba(${p.r},${p.g},${p.b},${alpha})`;
                ctx.fillRect(x, y, DOT_SIZE, DOT_SIZE);
            }

            ctx.restore();
            animRef.current = requestAnimationFrame(render);
        };

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = LOGO_URL;
        img.onload = () => {
            buildParticles(img);
            startNewCycle(performance.now());
            animRef.current = requestAnimationFrame(render);
        };

        return () => {
            if (animRef.current) cancelAnimationFrame(animRef.current);
        };
    }, []);

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px'
        }}>
            <canvas
                ref={canvasRef}
                width={520}
                height={520}
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    display: 'block'
                }}
            />
            {message && (
                <p style={{
                    fontSize: '15px',
                    color: 'rgba(255, 255, 255, 0.7)',
                    textAlign: 'center',
                    margin: 0,
                    maxWidth: '300px',
                    lineHeight: '1.5'
                }}>
                    {message}
                </p>
            )}
        </div>
    );
};

export default LogoLoader;
